import 'locomotive-scroll/dist/locomotive-scroll.css'
import LocomotiveScroll from 'locomotive-scroll'
import EmblaCarousel from 'embla-carousel'
import Fade from 'embla-carousel-fade'

// 本番・STG のみこの特集バンドル内の console 通常出力を抑止（console.error は維持）
;(() => {
  if (typeof window === 'undefined' || !window.location?.hostname) return
  const h = window.location.hostname
  if (
    h !== 'official-store.jfa.jp' &&
    h !== 'www.official-store.jfa.jp' &&
    h !== 'stg-official-store.jfa.jp'
  ) {
    return
  }
  const noop = () => {}
  const names = [
    'log',
    'debug',
    'info',
    'warn',
    'group',
    'groupCollapsed',
    'groupEnd',
    'trace',
    'table',
    'time',
    'timeEnd',
    'timeLog',
  ]
  names.forEach((name) => {
    if (typeof console[name] === 'function') {
      console[name] = noop
    }
  })
})()

// LocomotiveScrollのインスタンスを保持する変数
let locomotiveScroll

/**
 * ヘッダーの表示/非表示を制御する関数
 * スクロール方向に応じてヘッダーを表示・非表示にする
 */
const initHeaderToggle = () => {
  const header = document.getElementById('header');
  if (!header) return;

  let lastScrollY = window.scrollY;
  let ticking = false;

  // スクロール方向を検出してヘッダーの表示/非表示を切り替え
  const updateHeaderVisibility = () => {
    const currentScrollY = window.scrollY;

    // 下スクロール: ヘッダーを隠す
    if (currentScrollY > lastScrollY && currentScrollY > 100) {
      header.classList.add('is-hidden');
    }
    // 上スクロール: ヘッダーを表示
    else if (currentScrollY < lastScrollY) {
      header.classList.remove('is-hidden');
    }

    lastScrollY = currentScrollY;
    ticking = false;
  };

  // スクロールイベントをスロットリング
  const onScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(updateHeaderVisibility);
      ticking = true;
    }
  };

  window.addEventListener('scroll', onScroll, { passive: true });
};

/** フェードスライダー用ビューポートに付けるクラス（HTML と合わせる） */
const FADE_SLIDER_CLASS = 'js-embla-fade';

/** Locomotive の `data-scroll-event-progress` と同じイベント名（authentic セクション） */
const AUTHENTIC_PROGRESS_EVENT = 'ls-authentic-progress';

/** ギャラリー: `data-scroll-call` — 画面内に入ったら実画像を読み込み、noimage をフェードアウト */
const GALLERY_REVEAL_EVENT = 'ls-gallery-reveal';

/**
 * Locomotive の画面内検知（data-scroll-call）でギャラリー実画像を読み込み、表示完了後にフェードする
 * @param {CustomEvent} e
 */
const onGalleryReveal = (e) => {
  if (e.detail?.way !== 'enter') return;
  const li = e.detail?.target;
  if (!(li instanceof HTMLElement)) return;

  const media = li.querySelector('.gallery-item__media');
  const img = li.querySelector('.gallery-item__img');
  if (!(media instanceof HTMLElement) || !(img instanceof HTMLImageElement)) return;

  if (media.classList.contains('is-revealed')) return;

  const src = img.dataset.src;
  if (!src) {
    media.classList.add('is-revealed');
    return;
  }

  const reveal = () => {
    media.classList.add('is-revealed');
    locomotiveScroll?.resize();
  };

  img.removeAttribute('data-src');
  img.src = src;

  if (img.complete && img.naturalWidth > 0) {
    reveal();
    return;
  }

  img.addEventListener(
    'load',
    () => {
      reveal();
    },
    { once: true }
  );
  // error 時はプレースホルダのまま（is-revealed を付けない）
};

const emblaByViewport = new WeakMap();

/** @typedef {{ timerId: ReturnType<typeof setTimeout>, targetIndex: number }} SlideDelayPending */

/** @type {WeakMap<HTMLElement, SlideDelayPending>} */
const slideDelayPendingByViewport = new WeakMap();

/**
 * `data-slide-delay`（ms）。未指定・0・不正値は即時切り替え
 * @param {HTMLElement} viewport
 * @returns {number}
 */
const getSlideDelayMs = (viewport) => {
  const n = Number.parseInt(viewport.dataset.slideDelay, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const clearSlideDelayPending = (viewport) => {
  const state = slideDelayPendingByViewport.get(viewport);
  if (state?.timerId != null) {
    clearTimeout(state.timerId);
  }
  slideDelayPendingByViewport.delete(viewport);
};

/**
 * 目標スライド index を反映（data-slide-delay がある場合はその分遅らせる）
 * @param {HTMLElement} viewport
 * @param {Object} embla
 * @param {number} index
 */
const applySliderTargetIndex = (viewport, embla, index) => {
  const current = embla.selectedScrollSnap();
  const delayMs = getSlideDelayMs(viewport);

  if (index === current) {
    clearSlideDelayPending(viewport);
    return;
  }

  if (delayMs <= 0) {
    clearSlideDelayPending(viewport);
    embla.scrollTo(index);
    return;
  }

  const pending = slideDelayPendingByViewport.get(viewport);
  if (pending && pending.targetIndex === index && pending.timerId != null) {
    return;
  }

  clearSlideDelayPending(viewport);
  const timerId = setTimeout(() => {
    slideDelayPendingByViewport.delete(viewport);
    const api = emblaByViewport.get(viewport);
    if (!api) return;
    if (api.selectedScrollSnap() !== index) {
      api.scrollTo(index);
    }
  }, delayMs);
  slideDelayPendingByViewport.set(viewport, { timerId, targetIndex: index });
};

/**
 * スクロール progress（0〜1）を Embla のスライド index に変換する。
 * セクションのスクロール長を「画面」単位で 2n+1（n=スライド枚数）とし、
 * 例: n=2 のとき 0〜0.5画面は1枚目、0.5〜2画面が1枚目、2〜3.5画面が2枚目、3.5〜5画面は2枚目（余韻）。
 * @param {number} progress
 * @param {number} slideCount
 * @returns {number}
 */
const progressToEmblaIndexByScreenBuckets = (progress, slideCount) => {
  const n = Math.max(1, Math.floor(slideCount));
  const p = Math.min(1, Math.max(0, progress));
  if (n === 1) return 0;

  const totalScreens = 2 * n + 1;
  const s = p * totalScreens;

  if (s < 0.5 + 1.5) return 0;

  for (let j = 1; j <= n - 2; j += 1) {
    const end = 0.5 + 1.5 * (j + 1);
    if (s < end) return j;
  }

  return n - 1;
};

/** 最後の `.fixed-content` の後に挿入するスクロール用スペーサー（JS で生成） */
const FIXED_CONTENT_SCROLL_SPACER_CLASS = 'js-fixed-content-scroll-spacer';

/**
 * 最後の `.fixed-content` の直後に div を置き、`--top + --height` ぶんの高さを確保する
 * absolute 配置で通常フローから外れたぶんのスクロール領域を担保する
 */
const ensureFixedContentScrollSpacer = () => {
  const main = document.querySelector('main');
  if (!(main instanceof HTMLElement)) return;

  const fixedSections = main.querySelectorAll('.fixed-content');
  const lastFixedSection = fixedSections[fixedSections.length - 1];
  if (!(lastFixedSection instanceof HTMLElement)) return;

  let spacer = main.querySelector(`.${FIXED_CONTENT_SCROLL_SPACER_CLASS}`);
  if (!(spacer instanceof HTMLElement)) {
    spacer = document.createElement('div');
    spacer.className = FIXED_CONTENT_SCROLL_SPACER_CLASS;
    spacer.setAttribute('aria-hidden', 'true');
  }

  const styles = getComputedStyle(lastFixedSection);
  const top = Number.parseFloat(styles.getPropertyValue('--top').trim());
  const height = Number.parseFloat(styles.getPropertyValue('--height').trim());

  const safeTop = Number.isFinite(top) && top > 0 ? top : 0;
  const safeHeight = Number.isFinite(height) && height > 0 ? height : 0;
  const totalHeight = safeTop + safeHeight;

  if (totalHeight <= 0) {
    spacer.remove();
    locomotiveScroll.resize();
    return;
  }

  spacer.style.height = `calc(${totalHeight} * 1dvh)`;
  lastFixedSection.insertAdjacentElement('afterend', spacer);
  locomotiveScroll.resize();
};

/**
 * セクション内の `.js-embla-fade` を、スクロール progress に同期する
 * @param {HTMLElement} section
 * @param {number} progress
 */
const syncFadeSlidersToProgress = (section, progress) => {
  if (!(section instanceof HTMLElement)) return;

  section.querySelectorAll(`.${FADE_SLIDER_CLASS}`).forEach((viewport) => {
    const embla = emblaByViewport.get(viewport);
    if (!embla) return;

    const slideCount = embla.scrollSnapList().length;
    if (slideCount < 1) return;

    const targetIndex = progressToEmblaIndexByScreenBuckets(progress, slideCount);
    const index = Math.min(targetIndex, slideCount - 1);
    applySliderTargetIndex(viewport, embla, index);
  });
};

/**
 * embla-carousel-fade プラグインでフェード切り替えする Embla を初期化する
 * @param {HTMLElement} viewport - `.embla__viewport` 要素
 * @param {Object} options - Embla のオプション
 * @returns {Object|null} Embla インスタンス
 */
const initFadeSlider = (viewport, options = {}) => {
  if (!(viewport instanceof HTMLElement)) {
    console.warn('initFadeSlider: viewport must be an HTMLElement');
    return null;
  }

  // duration が 0 だと Fade プラグインは即時表示のみになりフェードが進まない（プラグイン実装に依存）
  const defaultOptions = {
    loop: true,
    duration: 30,
    watchDrag: false,
    dragFree: false,
    dragThreshold: 10,
    ...options
  };

  const embla = EmblaCarousel(viewport, defaultOptions, [Fade()]);
  emblaByViewport.set(viewport, embla);
  return embla;
};

/**
 * `.js-embla-fade` を付けた各ビューポートをまとめて初期化する
 * @param {Object} options - 全スライダー共通の Embla オプション
 * @returns {Object[]} 初期化に成功した Embla インスタンス
 */
const initFadeSliders = (options = {}) =>
  Array.from(document.getElementsByClassName(FADE_SLIDER_CLASS))
    .map((viewport) => initFadeSlider(viewport, options))
    .filter(Boolean);

/**
 * スマホでは慣性補間を実質オフにし、data-scroll 系の検知機能を優先する
 * @returns {Object}
 */
const getLenisOptions = () => {
  const isMobile = window.matchMedia('(max-width: 767px)').matches;
  if (!isMobile) {
    return {
      lerp: 0.09,
      duration: 1.8
    };
  }

  return {
    // 補間をほぼ無効化して、モバイルでの過剰な動きを抑える
    lerp: 1,
    duration: 0,
    smoothWheel: false,
    smoothTouch: false
  };
};

document.addEventListener('DOMContentLoaded', () => {
  console.log('JFA Special Page Loaded');

  // LocomotiveScrollを初期化
  locomotiveScroll = new LocomotiveScroll({
    lenisOptions: getLenisOptions()
  });

  window.addEventListener(GALLERY_REVEAL_EVENT, onGalleryReveal);

  // ヘッダーの表示/非表示機能を初期化
  initHeaderToggle();

  initFadeSliders();

  ensureFixedContentScrollSpacer();

  window.addEventListener('resize', ensureFixedContentScrollSpacer, { passive: true });

  // 前回のプログレス値をキャッシュして不要な処理をスキップ
  const progressCache = new WeakMap();

  window.addEventListener(AUTHENTIC_PROGRESS_EVENT, (e) => {
    const section = e.detail?.target;
    const progress = typeof e.detail?.progress === 'number' ? e.detail.progress : 0;
    if (section instanceof HTMLElement) {
      syncFadeSlidersToProgress(section, progress);
      // content: は <string> 必須のため、数値の --progress を表示用に引用符付きで複製する
      section.style.setProperty(
        '--progress-label',
        JSON.stringify(String(Math.round(progress * 10000) / 10000))
      );

      // fixed-content-inner へのクラス付与判定（パフォーマンス最適化版）
      const inner = section.querySelector('.fixed-content-inner');
      if (inner) {
        // progressを小数点第2位で丸めて比較（0.01刻みで判定）
        const roundedProgress = Math.round(progress * 100) / 100;

        // 前回と同じ値なら処理をスキップ（パフォーマンス向上）
        const lastProgress = progressCache.get(section);
        if (lastProgress !== roundedProgress) {
          progressCache.set(section, roundedProgress);

          // --height の値を取得して閾値を計算
          const styles = getComputedStyle(section);
          const height = Number.parseFloat(styles.getPropertyValue('--height').trim());

          // 閾値の計算: heightが100増えるごとに半分になる
          // height = 100 → threshold = 50 / 100 = 0.5
          // height = 200 → threshold = 50 / 200 = 0.25
          const threshold = Number.isFinite(height) && height > 0 ? 50 / height : 0.5;

          // 要素が完全にビューポート内に表示されている状態を判定
          const isFullyVisible = roundedProgress >= threshold && roundedProgress <= 1;
          const isSectionStart = roundedProgress >= (threshold + 0.1) &&  roundedProgress <= 1;

          if (isFullyVisible && !inner.classList.contains('is-fully-visible')) {
            inner.classList.add('is-fully-visible');
            console.log('fixed-content-inner が完全に表示されました (progress:', roundedProgress, 'threshold:', threshold, 'height:', height, ')');
          } else if (!isFullyVisible && inner.classList.contains('is-fully-visible')) {
            inner.classList.remove('is-fully-visible');
            console.log('fixed-content-inner が画面外に出ました (progress:', roundedProgress, ')');
          }
          if(isSectionStart && !inner.classList.contains('is-section-start')) {
            inner.classList.add('is-section-start');
            console.log('fixed-content-inner がセクション開始時に表示されました (progress:', roundedProgress, ')');
          } else if (!isSectionStart && inner.classList.contains('is-section-start')) {
            inner.classList.remove('is-section-start');
            console.log('fixed-content-inner がセクション開始時に表示されました (progress:', roundedProgress, ')');
          }
        }
      }
    }
  });

  // Locomotive の初回描画後に --progress を反映
  requestAnimationFrame(() => {
    ensureFixedContentScrollSpacer();
    const authentic = document.querySelector('.authentic.fixed-content');
    if (!(authentic instanceof HTMLElement)) return;
    const initial = Number.parseFloat(
      getComputedStyle(authentic).getPropertyValue('--progress').trim()
    );
    syncFadeSlidersToProgress(
      authentic,
      Number.isFinite(initial) ? initial : 0
    );
  });
});
